import React, { useEffect } from 'react';
import { Alert, Badge, Button, Col, OverlayTrigger, Table, Tooltip } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faBook, faFolderPlus, faSearch } from '@fortawesome/free-solid-svg-icons';
import { useHistory, useLocation, useParams } from 'react-router';

import Layout from '../Layout';
import Spinner from '../../components/Common/Spinner/Spinner';
import HistoryView from '../../components/History/HistoryView';
import HistoryListMobile from './HistoryListMobile';

import es from '../../lang/es';
import routes from '../../constants/routes';

import { selectHistoryAction, unselectJurisdictionAction } from '../../actions/proceedingActions';
import alertMessages from '../../helpers/alertMessages';
import messages from '../../constants/messages';
import colors from '../../constants/colors';
import { addProceedingAction } from '../../actions/authActions';
import apiMethods from '../../constants/apiMethods';
import api from '../../helpers/api';
import { getApiPrefix } from '../../helpers/authHelper';

const HistoryList = () => {
    //Inicializo el dispatch
    const dispatch = useDispatch();
    //Inicio el history
    const history = useHistory();
    //Tomos los paramétros
    const params = useParams();
    //Tomo fuero y nro
    const { jurisdiction } = params;
    //Tomo los query params
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    //Busco el parámetro
    const v = query.get('v');
    //Tomo el store
    const authUser = useSelector(state => state.auth.user );
    const loading = useSelector(state => state.proceeding.loading);
    const proceeding = useSelector(state => state.proceeding.selectedProceeding);
    const stories = useSelector(state => state.proceeding.stories);
    const selectedHistory = useSelector(state => state.proceeding.selectedHistory);
    //Me fijo si hay proceso seleccionado
    if(!proceeding){
        //Redirecciono
        history.push(routes.SEARCH(jurisdiction));
    }
    //Manejo la apertura
    const handleOpen = story => {
        //Llamo al dispatch
        dispatch( selectHistoryAction(proceeding, story) );
    }
    //Manejo la redirección
    const handleNewSearch = () => {
        //Limpio
        dispatch( unselectJurisdictionAction() );
        //Redirecciono
        history.push(routes.SEARCH(jurisdiction));
    }
    //Tomo los cambios
    useEffect(() => {
        //Llamo al método
        handleOpenStory();
    }, [stories]);
    //Manejo la apertura automática de historia
    const handleOpenStory = () => {
        //Me fijo si hay stores, y si hay selección
        if(stories && v){
            //Busco para ver si está la historia seleccionada en el expediente
            const s = stories.find(s => parseInt(s.histid) === parseInt(v));
            //Me fijo si lo encuentra
            if(s){
                //Abro la historia
                handleOpen(s);
            }
        }
    }
    //Manejo el agregarlo a mis expedientes
    const handleAddProceeding = () => {
        //Consulto antes de agregar
        alertMessages.confirm({
            title: es.ADD_PROCEEDING,
            text: messages.ACTIONS.ADD_PROCEEDING_CONFIRM,
            confirmColor: colors.blue,
            confirmText: es.ACCEPT,
            callback: () => {
                //Llamo al dispatch
                dispatch( addProceedingAction(proceeding) );
            }
        });
    }
    //Manejo la apertura del flipbook
    const handleUrlBook = async e => {
        e.preventDefault();
        
        try{
            //Muestro espera
            alertMessages.waiting(messages.LOADING.GETTING_LINK);
            //Armo la data
            const data = {
                jurisdiction: proceeding.jurisdiction_id,
                proceeding: proceeding.procid
            }
            //Consulta
            const response = await api.post(`${getApiPrefix()}${apiMethods.HISTORY_FLIPBOOK}`, data);
            //Obtengo los datos
            const res = response.data;
            //Abro en nueva ventana la URL
            window.open(res.data.url);
            //Oculto la espera
            alertMessages.close();
        }catch(error){
            const response = error.response;
            let data = {
                data: null,
                message: messages.ERROR.SERVER
            };
            //Verifico si hay error
            if(response){
                data = response.data;
            }
            //Cierro la espera
            alertMessages.close();
            //Mensaje de error
            alertMessages.error(data.message);
        }
    }
    //Render
    return (
        <Spinner active={loading}>
        {
            proceeding ? (
                <Layout 
                    title={`${es.PROCEEDING} ${ proceeding.nro_expediente }`} 
                    subtitle={es.HISTORY}
                    items={[
                        {
                            title: proceeding.jurisdiction_description,
                            link: routes.SEARCH(jurisdiction)
                        },
                        {
                            title: proceeding.nro_expediente,
                            active: true
                        }
                    ]}
                    actions={[
                        {
                            title: es.BACK,
                            type: 'secondary',
                            icon: faArrowLeft,
                            handle: () => {
                                history.goBack()
                            },
                            visible: true
                        },
                        {
                            title: es.NEW_SEARCH,
                            type: 'primary',
                            icon: faSearch,
                            handle: handleNewSearch,
                            visible: true
                        },
                        {
                            title: es.ADD_PROCEEDING,
                            type: 'success',
                            icon: faFolderPlus,
                            handle: handleAddProceeding,
                            visible: authUser
                        }
                    ]}
                >
                    <Col xs={12}>
                        <Alert variant="warning">
                            <Badge variant="info">
                                <h5 className="mb-0"><strong>{ proceeding.nro_expediente}</strong></h5>
                            </Badge>
                            <br />
                            <h5 className="mb-0">{`${proceeding.acto} C/ ${proceeding.dema} S/ ${proceeding.tipo_proceso}`}</h5>
                            {
                                proceeding.juzgado ? (
                                    <small><strong>{ proceeding.juzgado.dscr.toUpperCase() }</strong></small>
                                ) : null
                            }
                            {
                                authUser && proceeding.url_book ? (
                                    <React.Fragment>
                                        <br />
                                        <Button variant='warning' size='sm' className='mt-2' onClick={handleUrlBook}>
                                            <FontAwesomeIcon icon={ faBook } /> <strong>{ es.VIEW_FLIPBOOK }</strong>
                                        </Button>
                                    </React.Fragment>
                                ) : null
                            }
                        </Alert>
                    </Col>

                    {
                        proceeding.ultimo_tramite ? (
                            <Col xs={12}>
                                <Alert variant="info">
                                    <i>{ proceeding.ultimo_tramite }</i>
                                </Alert>
                            </Col>
                        ) : null
                    }
                    
                    <Col xs={12}>
                        <Table striped size="sm" responsive className="d-none d-md-table">
                            <thead className="thead-dark">
                                <tr>
                                    <th>Fecha</th>
                                    <th>Descripción</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                            {
                                stories ? (
                                    stories.map(story => (
                                        <tr key={`ph${story.histid}`}>
                                            <td className="align-middle text-center">
                                                { story.fecha }
                                            </td>
                                            <td className="align-middle">
                                                { story.dscr }
                                            </td>
                                            <td className="align-middle text-center">
                                                <OverlayTrigger
                                                    key={`oh-${story.histid}`}
                                                    placement="left"
                                                    overlay={
                                                        <Tooltip id={`tooltip-${story.histid}`}>
                                                            { es.VIEW_TEXT.toUpperCase() }
                                                        </Tooltip>
                                                    }
                                                >
                                                <Button variant="info" size="sm" onClick={() => handleOpen(story)}>
                                                    <FontAwesomeIcon icon={ faSearch } />
                                                </Button>
                                                </OverlayTrigger>
                                            </td>
                                        </tr>
                                    ))
                                ) : null
                            }
                            </tbody>
                        </Table>
                        <HistoryListMobile
                            stories={stories}
                            handleOpen={handleOpen}
                        />
                    </Col>

                    {
                        selectedHistory ? (
                            <HistoryView
                                proceeding={proceeding}
                            />
                        ) : null
                    }
                </Layout>
            ) : null
        }
        </Spinner>
    );
};

export default HistoryList;